import { scaleTime } from '@visx/scale';
import { useState } from 'react';
import { formatAbsolute, formatDuration } from '../lib/format.ts';
import { ChartFrame, ChartTooltip } from './ChartFrame.tsx';
import type { ChartBaseProps } from './ChartFrame.tsx';
import { colourFor } from './palette.ts';

export interface TimelineSpan {
  id: string;
  /** The lane it runs in (a machine). */
  lane: string;
  start: number;
  end: number;
  label: string;
  /** Colour group (an agent kind): spans of one group share a data colour. */
  group: string;
  /** Moments on the span (a person was asked). */
  marks?: number[];
}

export interface TimelineChartProps extends ChartBaseProps {
  spans: TimelineSpan[];
  from: number;
  to: number;
  /** Lane labels by lane key (default the key). */
  laneLabel?: (lane: string) => string;
  groupLabel?: (group: string) => string;
  onSelect?: (id: string) => void;
  /** At most this many lanes, busiest first (default 12). */
  maxLanes?: number;
}

const LANE_H = 20;
const LABEL_W = 112;
const AXIS_H = 20;

/**
 * When work ran (data-viz.md, a Gantt row per lane): one bar per span from its start to its end,
 * coloured by group, small ticks where a person was asked. Hover or focus a bar for its times;
 * Enter opens it.
 */
export function TimelineChart({ spans, from, to, laneLabel = (l) => l, groupLabel = (g) => g, onSelect, maxLanes = 12, height: _h, ...base }: TimelineChartProps) {
  const [hover, setHover] = useState<TimelineSpan | null>(null);
  const counts = new Map<string, number>();
  for (const s of spans) counts.set(s.lane, (counts.get(s.lane) ?? 0) + 1);
  const lanes = [...counts.keys()].sort((a, b) => counts.get(b)! - counts.get(a)! || a.localeCompare(b)).slice(0, maxLanes);
  const groups = [...new Set(spans.map((s) => s.group))].sort();
  const shown = spans.filter((s) => lanes.includes(s.lane) && s.end > from && s.start < to);
  const height = Math.max(1, lanes.length) * (LANE_H + 4) + AXIS_H;
  const legend = groups.map((g) => ({ key: g, label: groupLabel(g), colour: colourFor(g, groups) }));
  const table = { columns: ['Lane', 'Session', 'Started', 'Ended'], rows: shown.map((s) => [laneLabel(s.lane), s.label, formatAbsolute(s.start), formatAbsolute(s.end)]) };

  return (
    <ChartFrame {...base} height={height} isEmpty={shown.length === 0} legend={legend.length > 1 ? legend : undefined} table={table}>
      {(width) => {
        const iw = Math.max(0, width - LABEL_W);
        const x = scaleTime<number>({ domain: [new Date(from), new Date(to)], range: [0, iw] });
        const ticks = x.ticks(Math.max(2, Math.min(6, Math.floor(iw / 96))));
        const fmt = x.tickFormat(ticks.length);
        return (
          <>
            <svg width={width} height={height} className="overflow-visible">
              {lanes.map((lane, i) => (
                <g key={lane} transform={`translate(0, ${i * (LANE_H + 4)})`}>
                  <text x={0} y={LANE_H / 2} dominantBaseline="central" className="fill-fg-subtle text-caption">
                    {laneLabel(lane).slice(0, 16)}
                  </text>
                  <rect x={LABEL_W} y={0} width={iw} height={LANE_H} rx={2} fill="var(--surface-hover)" opacity={0.5} />
                </g>
              ))}
              {shown.map((s) => {
                const row = lanes.indexOf(s.lane);
                const x0 = x(new Date(Math.max(from, s.start)));
                const x1 = x(new Date(Math.min(to, s.end)));
                const y = row * (LANE_H + 4);
                return (
                  <g
                    key={s.id}
                    tabIndex={0}
                    role="img"
                    aria-label={`${s.label}, ${formatAbsolute(s.start)} to ${formatAbsolute(s.end)}`}
                    onMouseEnter={() => setHover(s)}
                    onMouseLeave={() => setHover(null)}
                    onFocus={() => setHover(s)}
                    onBlur={() => setHover(null)}
                    onClick={() => onSelect?.(s.id)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') onSelect?.(s.id);
                    }}
                    opacity={hover && hover.id !== s.id ? 0.4 : 1}
                    className={onSelect ? 'cursor-pointer outline-none' : 'outline-none'}
                  >
                    <rect x={LABEL_W + x0} y={y + 3} width={Math.max(2, x1 - x0)} height={LANE_H - 6} rx={2} fill={colourFor(s.group, groups)} />
                    {(s.marks ?? []).map((m) => (
                      <rect key={m} x={LABEL_W + x(new Date(m)) - 1} y={y} width={2} height={LANE_H} fill="var(--status-attention)" />
                    ))}
                  </g>
                );
              })}
              {ticks.map((t) => (
                <text key={t.getTime()} x={LABEL_W + x(t)} y={height - 4} textAnchor="middle" className="fill-fg-subtle text-caption num">
                  {fmt(t)}
                </text>
              ))}
            </svg>
            {hover && (
              <ChartTooltip
                width={width}
                left={LABEL_W + x(new Date(Math.max(from, hover.start)))}
                top={lanes.indexOf(hover.lane) * (LANE_H + 4) + LANE_H / 2}
                heading={hover.label}
                rows={[
                  { key: 'when', label: formatAbsolute(hover.start), colour: colourFor(hover.group, groups), value: formatDuration(hover.end - hover.start) },
                  ...(hover.marks?.length ? [{ key: 'asked', label: 'Asked a person', colour: 'var(--status-attention)', value: String(hover.marks.length) }] : []),
                ]}
              />
            )}
          </>
        );
      }}
    </ChartFrame>
  );
}
